
import React, { useState } from 'react';
import { Empresa } from '../../types';
import { produtosService } from '../../services/produtos.service';

const DIAS_SEMANA = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];

interface AdminConfiguracoesProps {
  currentEmpresa: Empresa | null;
  setCurrentEmpresa: (emp: Empresa | null) => void;
  handleSaveConfigGeral: () => void;
  handleUpdateHorario: (dia: string, campo: 'abertura' | 'fechamento', valor: string) => void;
  horariosParsed: any;
  isUpdating: boolean;
  novaSenha: string;
  setNovaSenha: (senha: string) => void;
  isDemo?: boolean;
}

export const AdminConfiguracoes: React.FC<AdminConfiguracoesProps> = ({
  currentEmpresa,
  setCurrentEmpresa,
  handleSaveConfigGeral,
  handleUpdateHorario,
  horariosParsed,
  isUpdating,
  novaSenha,
  setNovaSenha,
  isDemo = false
}) => {
  const [uploading, setUploading] = useState(false);

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isDemo) return;
    const file = e.target.files?.[0];
    if (file && currentEmpresa) {
      setUploading(true);
      try {
        const url = await produtosService.uploadImage(file);
        setCurrentEmpresa({ ...currentEmpresa, logo_url: url });
      } catch (err: any) {
        alert("Erro no upload: " + err.message);
      } finally {
        setUploading(false);
      }
    }
  };

  return (
    <div className="p-10 animate-fade-in flex flex-col items-center gap-10 pb-24">
      <div className="w-full max-w-4xl bg-white p-12 rounded-[3.5rem] border border-slate-200 shadow-sm space-y-12">
         <div className="flex justify-between items-center">
            <div>
               <h3 className="text-3xl font-black tracking-tight">Perfil do Restaurante</h3>
               <p className="text-slate-400 font-bold text-sm mt-1">Personalize como os clientes veem sua marca.</p>
            </div>
            <div className="w-24 h-24 bg-slate-50 border border-slate-100 rounded-[2rem] overflow-hidden shadow-inner flex items-center justify-center">
               {currentEmpresa?.logo_url ? (
                 <img src={currentEmpresa.logo_url} className="w-full h-full object-cover" />
               ) : (
                 <svg className="w-10 h-10 text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
               )}
            </div>
         </div>

         <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div className="space-y-8">
               <h4 className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                 <span className="w-1.5 h-4 bg-orange-600 rounded-full"></span>
                 Informações Gerais
               </h4>
               <div className="space-y-5">
                  <div>
                     <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Nome Comercial</label>
                     <input disabled={isDemo} className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold focus:border-orange-600 outline-none transition-all" value={currentEmpresa?.nome || ''} onChange={(e) => setCurrentEmpresa(currentEmpresa ? {...currentEmpresa, nome: e.target.value} : null)} />
                  </div>
                  <div>
                     <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Logotipo do Restaurante</label>
                     <div className="flex items-center gap-4 p-4 bg-slate-50 border border-slate-200 rounded-2xl">
                        <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" id="logo-upload" disabled={isDemo} />
                        <label htmlFor="logo-upload" className={`inline-flex items-center gap-2 bg-slate-900 text-white px-5 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-orange-600 transition-all ${isDemo ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}>
                           {uploading ? 'Enviando...' : 'Trocar Logotipo'}
                        </label>
                     </div>
                  </div>
                  <div>
                     <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">WhatsApp de Vendas</label>
                     <input disabled={isDemo} className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold focus:border-orange-600 outline-none transition-all" placeholder="5561999999999" value={currentEmpresa?.telefone_whatsapp || ''} onChange={(e) => setCurrentEmpresa(currentEmpresa ? {...currentEmpresa, telefone_whatsapp: e.target.value} : null)} />
                  </div>
                  <div>
                     <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Descrição curta</label>
                     <textarea rows={3} disabled={isDemo} className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold focus:border-orange-600 outline-none transition-all resize-none" value={currentEmpresa?.descricao || ''} onChange={(e) => setCurrentEmpresa(currentEmpresa ? {...currentEmpresa, descricao: e.target.value} : null)} />
                  </div>
               </div>

               <h4 className="text-[11px] font-black text-slate-400 uppercase tracking-widest pt-4 flex items-center gap-2">
                 <span className="w-1.5 h-4 bg-slate-900 rounded-full"></span>
                 Segurança do Painel
               </h4>
               <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Alterar Senha de Acesso</label>
                  <input type="password" disabled={isDemo} placeholder="Digite uma nova senha para mudar" className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold focus:border-orange-600 outline-none transition-all" value={novaSenha} onChange={(e) => setNovaSenha(e.target.value)} />
               </div>
            </div>

            <div className="space-y-8">
               <h4 className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                 <span className="w-1.5 h-4 bg-orange-600 rounded-full"></span>
                 Horários de Funcionamento
               </h4>
               <div className="bg-slate-50 p-8 rounded-[2.5rem] border border-slate-100 space-y-5">
                  {DIAS_SEMANA.map(dia => (
                    <div key={dia} className="grid grid-cols-5 items-center gap-3">
                       <span className="col-span-2 text-[10px] font-black text-slate-500 uppercase tracking-widest">{dia}</span>
                       <input 
                         type="text" 
                         disabled={isDemo}
                         placeholder="08:00" 
                         className="col-span-1.5 px-3 py-2 bg-white border border-slate-200 rounded-xl text-[10px] font-bold text-center outline-none focus:border-orange-600 transition-all"
                         value={horariosParsed[dia]?.abertura || ''}
                         onChange={(e) => handleUpdateHorario(dia, 'abertura', e.target.value)}
                       />
                       <span className="text-center text-slate-300 font-black">-</span>
                       <input 
                         type="text" 
                         disabled={isDemo}
                         placeholder="22:00" 
                         className="col-span-1.5 px-3 py-2 bg-white border border-slate-200 rounded-xl text-[10px] font-bold text-center outline-none focus:border-orange-600 transition-all"
                         value={horariosParsed[dia]?.fechamento || ''}
                         onChange={(e) => handleUpdateHorario(dia, 'fechamento', e.target.value)}
                       />
                    </div>
                  ))}
                  <div className="pt-4 flex items-center gap-2 justify-center">
                     <svg className="w-4 h-4 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                     <p className="text-[9px] text-slate-400 font-bold italic">Campos vazios indicam "Fechado".</p>
                  </div>
               </div>
            </div>
         </div>

         <div className="pt-8 border-t border-slate-50 flex justify-end">
            <button onClick={handleSaveConfigGeral} disabled={isUpdating || uploading} className="px-12 py-6 bg-slate-900 text-white font-black rounded-[2rem] shadow-2xl uppercase tracking-widest text-[11px] hover:bg-orange-600 transition-all disabled:opacity-50 flex items-center gap-4">
               {isUpdating || uploading ? (
                 <>
                   <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                   Processando...
                 </>
               ) : (
                 <>
                   <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
                   Publicar Atualizações
                 </>
               )}
            </button>
         </div>
      </div>
    </div>
  );
};
